import { supabase } from '@/shared/lib/supabase';
import { isProjectStatus } from '@/shared/constants/statuses';
import type { ReviewHistoryEntry, ReviewQueueItem } from '@/shared/types/domain';

/**
 * Única camada de `review` que conhece o supabase-js (architecture.md §5.1).
 *
 * **Só leitura, e agora sem exceção**: a fila parou de escrever qualquer coisa
 * quando a reivindicação de análise saiu da esteira (decisão 25) — clicar numa
 * linha só navega. As RPCs de transição vivem em
 * `features/projects/api/projects.api.ts` (spec-project-review.md decisão 12):
 * o painel de decisão é da tela do projeto, não desta feature.
 */

/**
 * Verbos que contam como decisão de análise no histórico (decisão 28).
 *
 * `cancelled` entra porque muda compromisso e interessa a quem aprovou.
 * **`completed` fica de fora**: é rotina do ministério, não decisão de análise
 * — e, pela decisão 29, ninguém chega nele na v1.
 */
const HISTORY_STATUSES = ['approved', 'rejected', 'changes_requested', 'cancelled'] as const;

/**
 * Teto de linhas. `+1` na consulta é o que revela o truncamento sem uma
 * segunda ida ao banco — a tela precisa saber para dizer "mais de 50".
 */
export const REVIEW_HISTORY_LIMIT = 50;

const HISTORY_WINDOW_DAYS = 30;

/** O que a view `review_queue` devolve, antes da estreitagem. */
type ReviewQueueRow = {
  id: string | null;
  ministry_id: string | null;
  ministry_name: string | null;
  coordination_id: string | null;
  coordination_name: string | null;
  branch_id: string | null;
  title: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  starts_at: string | null;
  ends_at: string | null;
  expected_audience: number | null;
  responsible_profile_id: string | null;
  responsible_name: string | null;
  estimated_budget_cents: number | null;
  funding_source_id: string | null;
  funding_source_name: string | null;
  budget_notes: string | null;
  extra_info: string | null;
  status: string | null;
  version: number | null;
  created_by: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  closed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  space_names: string[] | null;
};

/**
 * Estreita uma linha de `review_queue` — mesma razão de `narrow()` em
 * `features/projects/api/projects.api.ts`: view não prova coluna não-nula, e
 * uma linha sem a espinha dorsal (identidade, cadeia organizacional, situação)
 * é descartada, não remendada.
 */
function narrow(row: ReviewQueueRow): ReviewQueueItem | null {
  const {
    id,
    ministry_id,
    ministry_name,
    coordination_id,
    coordination_name,
    branch_id,
    status,
    created_by,
    created_at,
    updated_at,
    version,
  } = row;

  if (
    id === null ||
    ministry_id === null ||
    ministry_name === null ||
    coordination_id === null ||
    coordination_name === null ||
    branch_id === null ||
    status === null ||
    !isProjectStatus(status) ||
    created_by === null ||
    created_at === null ||
    updated_at === null ||
    version === null
  ) {
    return null;
  }

  return {
    id,
    ministry_id,
    ministry_name,
    coordination_id,
    coordination_name,
    branch_id,
    title: row.title,
    description: row.description,
    category_id: row.category_id,
    category_name: row.category_name,
    starts_at: row.starts_at,
    ends_at: row.ends_at,
    expected_audience: row.expected_audience,
    responsible_profile_id: row.responsible_profile_id,
    responsible_name: row.responsible_name,
    estimated_budget_cents: row.estimated_budget_cents,
    funding_source_id: row.funding_source_id,
    funding_source_name: row.funding_source_name,
    budget_notes: row.budget_notes,
    extra_info: row.extra_info,
    status,
    version,
    created_by,
    submitted_at: row.submitted_at,
    decided_at: row.decided_at,
    closed_at: row.closed_at,
    created_at,
    updated_at,
    space_names: row.space_names ?? [],
  };
}

/**
 * A fila inteira (US-18): o estado de espera, no escopo de quem chama.
 *
 * **Um status, não dois** (ADR-013). A cláusula de "tolerância a dado legado"
 * da 2ª rodada (§3.1) perdeu o objeto: o dado legado foi convertido pela
 * migration, e o valor que sobrou é o único que a esteira produz.
 *
 * Sem filtro nem ordenação aqui — a ordem por `submitted_at` é leitura da TELA
 * (`/analise`), porque a mesma linha crua também alimenta o contador da
 * sidebar, que não tem ordem nenhuma.
 */
export async function fetchReviewQueue(): Promise<ReviewQueueItem[]> {
  const { data, error } = await supabase.from('review_queue').select('*');
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    const item = narrow(row as ReviewQueueRow);
    return item ? [item] : [];
  });
}

/**
 * "Decidido nos últimos 30 dias" (decisão 28) — o que faz a fila vazia deixar
 * de ser um beco.
 *
 * **Nem migration nem view novas.** `project_status_history` já tem RLS
 * espelhando `projects_select`, então a janela já vem recortada pelo escopo de
 * quem olha: são as decisões de TODOS os revisores que a pessoa alcança, e não
 * só as dela. O embed de `projects` é o mesmo padrão de
 * `fetchProjectSupportItems`, e `!inner` garante que projeto invisível pela RLS
 * derrube a linha de histórico junto — sem ele, um título nulo passaria como
 * "Sem título" e viraria vazamento de existência.
 */
export async function fetchReviewHistory(): Promise<ReviewHistoryEntry[]> {
  const since = new Date(Date.now() - HISTORY_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('project_status_history')
    .select('project_id, to_status, actor_profile_id, actor_name, created_at, projects!inner(title)')
    .gte('created_at', since)
    .in('to_status', HISTORY_STATUSES)
    .order('created_at', { ascending: false })
    .limit(REVIEW_HISTORY_LIMIT + 1);
  if (error) throw error;

  return (data ?? []).flatMap((row) => {
    if (!isProjectStatus(row.to_status)) return [];
    return [
      {
        project_id: row.project_id,
        title: row.projects.title,
        to_status: row.to_status,
        actor_profile_id: row.actor_profile_id,
        actor_name: row.actor_name,
        created_at: row.created_at,
      },
    ];
  });
}
